/* eslint-disable arrow-body-style */
import React, {
  memo,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import store from '../../../../../../store';
import PhoneNumberListHeader from './components/phone-number-list-header';
import PhoneNumberListTable from './components/phone-number-list-table';
import PhoneNumberFiltersModal, {
  PhoneNumberFilters,
} from './components/phone-number-filters-modal';
import history from '../../../../../../shared/history';
import { AnalyticsEvents } from '../../../../../../shared/enums/analytics';
import {
  getUserShAccountId,
  getUserTrackingId,
} from '../../../../../../shared/utils/user-details';
import {
  GetPhoneNumbersListPayload,
  PhoneNumber,
  PhoneNumberListResponse,
  PhoneNumberSettingCode,
  PhoneNumberSettingsActionType,
} from '../../../../types/phone-numbers';
import {
  PhoneNumberFilters as ReduxPhoneNumberFilters,
  resetAssignPhoneNumberRequest,
  resetBulkAssignPhoneNumberRequest,
  resetSetPhoneNumberLabelRequest,
  resetBulkSetPhoneNumberLabelRequest,
  resetDeletePhoneNumberRequest,
  resetRevokePhoneNumberReleaseRequest,
  resetBulkUpdatePhoneNumberSettingsRequest,
  resetUpdatePhoneNumberSettingsRequest,
} from '../../../../dialer-slice';
import { executeOnRequestStatus } from '../../../../../../shared/utils';
import toaster, { Theme } from '../../../../../../shared/toaster';
import {
  getDialerPhoneNumberListRequest,
  getPhoneNumberLabelsRequest,
} from '../../../../extra-actions';
import { RootState } from '../../../../../../store/root-reducer';
import CallLogsSearchEmptyState from '../../../call-logs/components/call-logs-tab-empty-state-templates/call-logs-search-empty-state';
import PurchasePhoneNumberBanner from '../purchase-phone-number-banner/purchase-phone-number-banner';

export type SelectedPhoneNumber = {
  id: number;
  phoneNumber: string;
};

interface PhoneNumberListProps {
  myPhoneNumbersList: PhoneNumberListResponse | null;
  isGetMyPhoneNumbersListRequestPending: boolean;
  fetchPhoneNumbers: (payload: GetPhoneNumbersListPayload) => void;
  myPhoneNumbersListFilters: ReduxPhoneNumberFilters;
  updateFilters: (filters: ReduxPhoneNumberFilters) => void;
  sendGetAdminSettingsRequest: () => void;
  sendGetCountryCodeOptionsListForFiltersRequest: () => void;
  sendGetRegionOptionsListForFiltersRequest: () => void;
  sendGetTeamMembersList: () => void;
  countriesOptionsForNumbersSearch: string[] | null;
  teamMembersList: any[] | null;
  regionOptionsListForFilters: any[] | null;
  assignPhoneNumberRequestState: any;
  bulkAssignPhoneNumberRequestState: any;
  setPhoneNumberLabelRequestStatus: any;
  bulkSetPhoneNumberLabelRequestStatus: any;
  deletePhoneNumberRequestStatus: any;
  revokePhoneNumberReleaseRequestStatus: any;
}
const PhoneNumberList: React.FC<PhoneNumberListProps> = memo(
  ({
    myPhoneNumbersList,
    isGetMyPhoneNumbersListRequestPending,
    fetchPhoneNumbers,
    myPhoneNumbersListFilters,
    updateFilters,
    sendGetAdminSettingsRequest,
    sendGetCountryCodeOptionsListForFiltersRequest,
    sendGetRegionOptionsListForFiltersRequest,
    sendGetTeamMembersList,
    countriesOptionsForNumbersSearch,
    teamMembersList,
    regionOptionsListForFilters,
    assignPhoneNumberRequestState,
    bulkAssignPhoneNumberRequestState,
    setPhoneNumberLabelRequestStatus,
    bulkSetPhoneNumberLabelRequestStatus,
    deletePhoneNumberRequestStatus,
    revokePhoneNumberReleaseRequestStatus,
  }) => {
    const dispatch = useDispatch();
    const bulkUpdatePhoneNumberSettingsRequestState = useSelector(
      (state: RootState) => state.dialer.bulkUpdatePhoneNumberSettingsRequest,
    );
    const updatePhoneNumberSettingsRequestStates = useSelector(
      (state: RootState) => state.dialer.updatePhoneNumberSettingsRequest,
    );
    const [search, setSearch] = useState('');
    const [pagination, setPagination] = useState({
      page: 1,
      limit: 25,
      totalElements: 0,
    });
    const previousLimitRef = useRef(pagination.limit);

    // Selection state
    const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<
      SelectedPhoneNumber[]
    >([]);
    const [deSelectedPhoneNumbers, setDeSelectedPhoneNumbers] = useState<
      SelectedPhoneNumber[]
    >([]);
    const [isSelectAllAcrossPages, setIsSelectAllAcrossPages] = useState(false);

    const [
      settingsActionType,
      setSettingsActionType,
    ] = useState<PhoneNumberSettingsActionType | null>(null);

    // Filter state
    const [showFiltersModal, setShowFiltersModal] = useState(false);

    // Modal states to track if modals are open (for conditional refresh)
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showAssignLabelModal, setShowAssignLabelModal] = useState(false);

    // Calculate active filter count
    const activeFilterCount = useMemo(() => {
      let count = 0;
      if (myPhoneNumbersListFilters.countryCodes.length > 0) count += 1;
      if (myPhoneNumbersListFilters.regions.length > 0) count += 1;
      if (myPhoneNumbersListFilters.teamMemberIds.length > 0) count += 1;
      if (
        myPhoneNumbersListFilters.purchaseDate &&
        myPhoneNumbersListFilters.purchaseDate.length > 0
      )
        count += 1;
      return count;
    }, [myPhoneNumbersListFilters]);

    useEffect(() => {
      sendGetAdminSettingsRequest();
      sendGetCountryCodeOptionsListForFiltersRequest();
      sendGetTeamMembersList();
      sendGetRegionOptionsListForFiltersRequest();
    }, []);
    // Fetch data on mount and when pagination/search/filters change
    useEffect(() => {
      const payload: GetPhoneNumbersListPayload = {
        pageNumber: pagination.page,
        pageSize: pagination.limit,
        ...(search && { searchText: search }),
        ...(myPhoneNumbersListFilters.countryCodes.length > 0 && {
          countryCodes: myPhoneNumbersListFilters.countryCodes,
        }),
        ...(myPhoneNumbersListFilters.regions.length > 0 && {
          regions: myPhoneNumbersListFilters.regions,
        }),
        ...(myPhoneNumbersListFilters.teamMemberIds.length > 0 && {
          teamMemberIds: myPhoneNumbersListFilters.teamMemberIds,
        }),
        ...(myPhoneNumbersListFilters.purchaseDate &&
          myPhoneNumbersListFilters.purchaseDate.length > 0 &&
          (() => {
            const state = store.getState();
            const { timeZone } = state?.myProfile?.myProfile || {};

            const startDate = myPhoneNumbersListFilters.purchaseDate[0];
            const endDate = myPhoneNumbersListFilters.purchaseDate[1];

            if (!startDate || !endDate) return {};

            // Convert to DateTime with timezone and format with start/end of day
            const startDateTime = timeZone
              ? DateTime.fromJSDate(startDate).setZone(timeZone).set({
                  hour: 0,
                  minute: 0,
                  second: 0,
                  millisecond: 0,
                })
              : DateTime.fromJSDate(startDate).set({
                  hour: 0,
                  minute: 0,
                  second: 0,
                  millisecond: 0,
                });

            const endDateTime = timeZone
              ? DateTime.fromJSDate(endDate).setZone(timeZone).set({
                  hour: 23,
                  minute: 59,
                  second: 59,
                  millisecond: 999,
                })
              : DateTime.fromJSDate(endDate).set({
                  hour: 23,
                  minute: 59,
                  second: 59,
                  millisecond: 999,
                });

            return {
              purchaseDateStart: startDateTime.toISO(),
              purchaseDateEnd: endDateTime.toISO(),
            };
          })()),
      };
      fetchPhoneNumbers(payload);
    }, [
      pagination.page,
      pagination.limit,
      search,
      myPhoneNumbersListFilters,
      fetchPhoneNumbers,
    ]);

    // Update pagination totalElements when data changes
    useEffect(() => {
      if (myPhoneNumbersList?.total !== undefined) {
        setPagination((prev) => ({
          ...prev,
          totalElements: myPhoneNumbersList.total,
        }));
      }
    }, [myPhoneNumbersList?.total]);

    // Handle clear selection
    const handleClearSelection = useCallback(() => {
      setIsSelectAllAcrossPages(false);
      setSelectedPhoneNumbers([]);
      setDeSelectedPhoneNumbers([]);
    }, []);

    const onSearchChangeHandler = (newSearch: string) => {
      setSearch(newSearch);
      handleClearSelection();
      // Reset to first page when search changes
      setPagination((prev) => ({ ...prev, page: 1 }));
    };
    // Handle clear selection

    const handleFilterClick = () => {
      setShowFiltersModal(true);
      handleClearSelection();
    };

    const handleFiltersChange = (filters: PhoneNumberFilters) => {
      updateFilters(filters);
      // Reset to first page when filters change
      setPagination((prev) => ({ ...prev, page: 1 }));
    };

    const handleCloseFiltersModal = () => {
      setShowFiltersModal(false);
    };

    const handlePurchaseClick = () => {
      window.analytics?.track?.({
        userId: getUserTrackingId(),
        event: AnalyticsEvents.DialerNumberPurchaseFlowStarted,
        properties: {
          shAccountId: getUserShAccountId(),
        },
      });
      history.push('/dialer/phone-numbers/purchase');
    };
    // Handle pagination change from table
    const handlePaginationChange = useCallback(
      (options: any) => {
        const { page, limit, totalElements } = options;
        const newLimit = limit !== undefined ? limit : pagination.limit;
        const limitChanged = newLimit !== previousLimitRef.current;

        // If limit changed, always reset to page 1
        const newPage = limitChanged
          ? 1
          : page !== undefined
          ? page
          : pagination.page;

        previousLimitRef.current = newLimit;
        if (limitChanged) {
          handleClearSelection();
        }

        setPagination({
          page: newPage,
          limit: newLimit,
          totalElements:
            totalElements !== undefined
              ? totalElements
              : pagination.totalElements,
        });
      },
      [pagination],
    );

    // Get current page phone numbers data
    const currentPagePhoneNumbers = useMemo(() => {
      return myPhoneNumbersList?.phoneNumbers || [];
    }, [myPhoneNumbersList?.phoneNumbers]);

    // Transform selectedPhoneNumbers to ID array for TanStackTable
    // When isSelectAllAcrossPages is true, include all current page IDs (excluding deselected ones)
    const selectedRowIds = useMemo(() => {
      if (isSelectAllAcrossPages) {
        // In "select all across pages" mode, all current page IDs are conceptually selected
        // except those in deSelectedPhoneNumbers
        const deselectedIdsSet = new Set(
          deSelectedPhoneNumbers.map((item) => item.id),
        );
        return currentPagePhoneNumbers
          .filter((phone) => !deselectedIdsSet.has(phone.id))
          .map((phone) => phone.id);
      }
      return selectedPhoneNumbers.map((item) => item.id);
    }, [
      selectedPhoneNumbers,
      isSelectAllAcrossPages,
      deSelectedPhoneNumbers,
      currentPagePhoneNumbers,
    ]);

    // Calculate effective select all display count (total - deselected)
    const selectAllDisplayCount = useMemo(() => {
      if (!isSelectAllAcrossPages) return null;
      const totalRows = pagination.totalElements || 0;
      const deselectedCount = deSelectedPhoneNumbers.length;
      return Math.max(0, totalRows - deselectedCount);
    }, [
      isSelectAllAcrossPages,
      pagination.totalElements,
      deSelectedPhoneNumbers.length,
    ]);

    // Reset select all across pages when all items are deselected
    useEffect(() => {
      if (
        isSelectAllAcrossPages &&
        pagination.totalElements > 0 &&
        deSelectedPhoneNumbers.length === pagination.totalElements
      ) {
        // All items are deselected, reset the selection state
        setIsSelectAllAcrossPages(false);
        setSelectedPhoneNumbers([]);
        setDeSelectedPhoneNumbers([]);
      }
    }, [
      isSelectAllAcrossPages,
      pagination.totalElements,
      deSelectedPhoneNumbers.length,
    ]);

    // Helper to find phone number by ID
    const findPhoneNumberById = useCallback(
      (id: number): PhoneNumber | undefined => {
        return currentPagePhoneNumbers.find((phone) => phone.id === id);
      },
      [currentPagePhoneNumbers],
    );

    // Handle individual phone number selection
    const handlePhoneNumberSelect = useCallback(
      (rowId: string | number, isSelect: boolean) => {
        const phoneId = Number(rowId);
        const phone = findPhoneNumberById(phoneId);
        if (!phone) return;

        const phoneNumberObj: SelectedPhoneNumber = {
          id: phone.id,
          phoneNumber: phone.phoneNumber,
        };

        if (isSelectAllAcrossPages) {
          // In "select all across pages" mode
          if (isSelect) {
            // Selecting: remove from deselected list
            setDeSelectedPhoneNumbers((prev) =>
              prev.filter((item) => item.id !== phoneId),
            );
          } else {
            // Deselecting: add to deselected list and remove from selected if present
            setDeSelectedPhoneNumbers((prev) => {
              const prevSet = new Set(prev.map((item) => item.id));
              if (!prevSet.has(phoneId)) {
                return [...prev, phoneNumberObj];
              }
              return prev;
            });
            setSelectedPhoneNumbers((prev) =>
              prev.filter((item) => item.id !== phoneId),
            );
          }
        } else if (isSelect) {
          // Individual selection mode - selecting
          setSelectedPhoneNumbers((prev) => {
            const prevSet = new Set(prev.map((item) => item.id));
            if (!prevSet.has(phoneId)) {
              return [...prev, phoneNumberObj];
            }
            return prev;
          });
        } else {
          // Individual selection mode - deselecting
          setSelectedPhoneNumbers((prev) =>
            prev.filter((item) => item.id !== phoneId),
          );
        }
      },
      [isSelectAllAcrossPages, findPhoneNumberById],
    );

    // Handle select/deselect all on current page
    const handlePhoneNumberSelectAll = useCallback(
      (rowIds: (string | number)[], isSelect: boolean) => {
        const phoneIds = rowIds.map((id) => Number(id));
        const phonesToProcess = phoneIds
          .map((id) => findPhoneNumberById(id))
          .filter((phone): phone is PhoneNumber => phone !== undefined);

        if (isSelectAllAcrossPages) {
          // In "select all across pages" mode
          if (isSelect) {
            // Selecting all: remove from deselected list
            const phoneIdsSet = new Set(phoneIds);
            setDeSelectedPhoneNumbers((prev) =>
              prev.filter((item) => !phoneIdsSet.has(item.id)),
            );
          } else {
            // Deselecting all: add to deselected list
            const newDeselected = phonesToProcess.map((phone) => ({
              id: phone.id,
              phoneNumber: phone.phoneNumber,
            }));
            setDeSelectedPhoneNumbers((prev) => {
              const prevIdsSet = new Set(prev.map((item) => item.id));
              const toAdd = newDeselected.filter(
                (item) => !prevIdsSet.has(item.id),
              );
              return [...prev, ...toAdd];
            });
            // Remove from selected if present
            const phoneIdsSet = new Set(phoneIds);
            setSelectedPhoneNumbers((prev) =>
              prev.filter((item) => !phoneIdsSet.has(item.id)),
            );
          }
        } else if (isSelect) {
          // Individual selection mode - selecting all
          setSelectedPhoneNumbers((prev) => {
            const prevIdsSet = new Set(prev.map((item) => item.id));
            const newSelections = phonesToProcess
              .map((phone) => ({
                id: phone.id,
                phoneNumber: phone.phoneNumber,
              }))
              .filter((item) => !prevIdsSet.has(item.id));
            return [...prev, ...newSelections];
          });
        } else {
          // Individual selection mode - deselecting all
          const phoneIdsSet = new Set(phoneIds);
          setSelectedPhoneNumbers((prev) =>
            prev.filter((item) => !phoneIdsSet.has(item.id)),
          );
        }
      },
      [isSelectAllAcrossPages, findPhoneNumberById],
    );

    // Handle select all across pages
    // TanStackTable calls this with no arguments when button is clicked
    const handleSelectAllAcrossPages = useCallback(() => {
      const shouldEnable = !isSelectAllAcrossPages;
      setIsSelectAllAcrossPages(shouldEnable);
      if (!shouldEnable) {
        // When deselecting, clear everything
        setSelectedPhoneNumbers([]);
        setDeSelectedPhoneNumbers([]);
      } else {
        // When enabling "select all across pages", clear explicit selections
        // All are conceptually selected
        setSelectedPhoneNumbers([]);
        setDeSelectedPhoneNumbers([]);
      }
    }, [isSelectAllAcrossPages]);

    const enableBulkActions = useMemo(() => {
      return selectedPhoneNumbers.length > 0 || isSelectAllAcrossPages;
    }, [selectedPhoneNumbers, isSelectAllAcrossPages]);

    // Refresh phone numbers list with current pagination, search, and filters
    const refreshPhoneNumbers = useCallback(() => {
      const payload: GetPhoneNumbersListPayload = {
        pageNumber: pagination.page,
        pageSize: pagination.limit,
        ...(search && { searchText: search }),
        ...(myPhoneNumbersListFilters.countryCodes.length > 0 && {
          countryCodes: myPhoneNumbersListFilters.countryCodes,
        }),
        ...(myPhoneNumbersListFilters.regions.length > 0 && {
          regions: myPhoneNumbersListFilters.regions,
        }),
        ...(myPhoneNumbersListFilters.teamMemberIds.length > 0 && {
          teamMemberIds: myPhoneNumbersListFilters.teamMemberIds,
        }),
        ...(myPhoneNumbersListFilters.purchaseDate &&
          myPhoneNumbersListFilters.purchaseDate.length > 0 &&
          (() => {
            const state = store.getState();
            const { timeZone } = state?.myProfile?.myProfile || {};

            const startDate = myPhoneNumbersListFilters.purchaseDate[0];
            const endDate = myPhoneNumbersListFilters.purchaseDate[1];

            if (!startDate || !endDate) return {};

            // Convert to DateTime with timezone and format with start/end of day
            const startDateTime = timeZone
              ? DateTime.fromJSDate(startDate).setZone(timeZone).set({
                  hour: 0,
                  minute: 0,
                  second: 0,
                  millisecond: 0,
                })
              : DateTime.fromJSDate(startDate).set({
                  hour: 0,
                  minute: 0,
                  second: 0,
                  millisecond: 0,
                });

            const endDateTime = timeZone
              ? DateTime.fromJSDate(endDate).setZone(timeZone).set({
                  hour: 23,
                  minute: 59,
                  second: 59,
                  millisecond: 999,
                })
              : DateTime.fromJSDate(endDate).set({
                  hour: 23,
                  minute: 59,
                  second: 59,
                  millisecond: 999,
                });

            return {
              purchaseDateStart: startDateTime.toISO(),
              purchaseDateEnd: endDateTime.toISO(),
            };
          })()),
      };
      fetchPhoneNumbers(payload);
    }, [
      pagination.page,
      pagination.limit,
      search,
      myPhoneNumbersListFilters,
      fetchPhoneNumbers,
    ]);

    // Listen to assign phone number request success
    useEffect(() => {
      executeOnRequestStatus({
        status: assignPhoneNumberRequestState?.status,
        onSuccess: () => {
          dispatch(resetAssignPhoneNumberRequest());
          dispatch(getDialerPhoneNumberListRequest({}));
          // Only refresh if modal is open (request was initiated from modal)
          if (showAssignModal) {
            refreshPhoneNumbers();
            handleClearSelection();
            setShowAssignModal(false); // Close modal after success
          }
          toaster.success('Number(s) re-assigned successfully', {
            theme: Theme.New,
          });
        },
      });
    }, [
      assignPhoneNumberRequestState?.status,
      showAssignModal,
      dispatch,
      refreshPhoneNumbers,
      handleClearSelection,
    ]);

    // Listen to bulk assign phone number request success
    useEffect(() => {
      executeOnRequestStatus({
        status: bulkAssignPhoneNumberRequestState?.status,
        onSuccess: () => {
          dispatch(resetBulkAssignPhoneNumberRequest());
          dispatch(getDialerPhoneNumberListRequest({}));
          // Only refresh if modal is open (request was initiated from modal)
          if (showAssignModal) {
            refreshPhoneNumbers();
            handleClearSelection();
            setShowAssignModal(false); // Close modal after success
          }
          toaster.success('Number(s) re-assigned successfully', {
            theme: Theme.New,
          });
        },
      });
    }, [
      bulkAssignPhoneNumberRequestState?.status,
      showAssignModal,
      dispatch,
      refreshPhoneNumbers,
      handleClearSelection,
    ]);

    // Listen to set phone number label request success
    useEffect(() => {
      executeOnRequestStatus({
        status: setPhoneNumberLabelRequestStatus?.status,
        onSuccess: () => {
          toaster.success('Phone Number Label changed successfully', {
            theme: Theme.New,
          });
          dispatch(getPhoneNumberLabelsRequest({}));
          dispatch(resetSetPhoneNumberLabelRequest());
          // Only refresh if modal is open (request was initiated from modal)
          if (showAssignLabelModal) {
            refreshPhoneNumbers();
            handleClearSelection();
            setShowAssignLabelModal(false); // Close modal after success
          }
        },
      });
    }, [
      setPhoneNumberLabelRequestStatus?.status,
      showAssignLabelModal,
      dispatch,
      refreshPhoneNumbers,
      handleClearSelection,
    ]);

    // Listen to bulk set phone number label request success
    useEffect(() => {
      executeOnRequestStatus({
        status: bulkSetPhoneNumberLabelRequestStatus?.status,
        onSuccess: () => {
          toaster.success('Phone Number Label changed successfully', {
            theme: Theme.New,
          });
          dispatch(resetBulkSetPhoneNumberLabelRequest());
          dispatch(getPhoneNumberLabelsRequest({}));
          // Only refresh if modal is open (request was initiated from modal)
          if (showAssignLabelModal) {
            refreshPhoneNumbers();
            handleClearSelection();
            setShowAssignLabelModal(false); // Close modal after success
          }
        },
      });
    }, [
      bulkSetPhoneNumberLabelRequestStatus?.status,
      showAssignLabelModal,
      dispatch,
      refreshPhoneNumbers,
      handleClearSelection,
    ]);

    // Listen to delete phone number request success
    useEffect(() => {
      executeOnRequestStatus({
        status: deletePhoneNumberRequestStatus?.status,
        onSuccess: () => {
          toaster.success('Phone number deleted successfully', {
            theme: Theme.New,
          });
          dispatch(resetDeletePhoneNumberRequest());
          refreshPhoneNumbers();
        },
        onFailed: () => {
          dispatch(resetDeletePhoneNumberRequest());
        },
      });
    }, [deletePhoneNumberRequestStatus?.status, dispatch, refreshPhoneNumbers]);

    // Listen to revoke phone number release request success
    useEffect(() => {
      executeOnRequestStatus({
        status: revokePhoneNumberReleaseRequestStatus?.status,
        onSuccess: () => {
          toaster.success('Phone number release revoked successfully', {
            theme: Theme.New,
          });
          dispatch(resetRevokePhoneNumberReleaseRequest());
          refreshPhoneNumbers();
        },
        onFailed: () => {
          dispatch(resetRevokePhoneNumberReleaseRequest());
        },
      });
    }, [
      revokePhoneNumberReleaseRequestStatus?.status,
      dispatch,
      refreshPhoneNumbers,
    ]);

    const ACTION_TYPE_TO_SETTING_CODE: Record<
      PhoneNumberSettingsActionType,
      PhoneNumberSettingCode
    > = {
      [PhoneNumberSettingsActionType.CALL_RECORDING]:
        PhoneNumberSettingCode.CALL_RECORDING_ENABLED,
      [PhoneNumberSettingsActionType.VOICEMAIL]:
        PhoneNumberSettingCode.VOICEMAIL_ENABLED,
      [PhoneNumberSettingsActionType.CALL_FORWARDING]:
        PhoneNumberSettingCode.FORWARDING_ENABLED,
    };

    const ACTION_TYPE_TO_MESSAGE: Record<
      PhoneNumberSettingsActionType,
      string
    > = {
      [PhoneNumberSettingsActionType.CALL_RECORDING]:
        'Call recording set successfully',
      [PhoneNumberSettingsActionType.VOICEMAIL]:
        'Voicemail greeting set successfully',
      [PhoneNumberSettingsActionType.CALL_FORWARDING]:
        'Call forwarding rule set successfully',
    };

    const handleSettingsUpdateSuccess = useCallback(
      (resetAction: () => void) => {
        resetAction();
        if (settingsActionType) {
          toaster.success(ACTION_TYPE_TO_MESSAGE[settingsActionType], {
            theme: Theme.New,
          });
        }
        refreshPhoneNumbers();
        handleClearSelection();
        setSettingsActionType(null);
      },
      [settingsActionType, refreshPhoneNumbers, handleClearSelection],
    );

    // Bulk Phone Number Setting Update
    useEffect(() => {
      executeOnRequestStatus({
        status: bulkUpdatePhoneNumberSettingsRequestState?.status,
        onSuccess: () =>
          handleSettingsUpdateSuccess(() =>
            dispatch(resetBulkUpdatePhoneNumberSettingsRequest()),
          ),
      });
    }, [
      bulkUpdatePhoneNumberSettingsRequestState?.status,
      dispatch,
      handleSettingsUpdateSuccess,
    ]);

    // Single Phone Number Setting Update
    useEffect(() => {
      if (!settingsActionType) return;

      const settingCode = ACTION_TYPE_TO_SETTING_CODE[settingsActionType];
      const status =
        updatePhoneNumberSettingsRequestStates[settingCode]?.status;

      executeOnRequestStatus({
        status,
        onSuccess: () =>
          handleSettingsUpdateSuccess(() =>
            dispatch(resetUpdatePhoneNumberSettingsRequest(settingCode)),
          ),
      });
    }, [
      updatePhoneNumberSettingsRequestStates,
      settingsActionType,
      dispatch,
      handleSettingsUpdateSuccess,
    ]);
    if (
      myPhoneNumbersList?.total === 0 &&
      !isGetMyPhoneNumbersListRequestPending &&
      search === '' &&
      activeFilterCount === 0
    ) {
      return <PurchasePhoneNumberBanner />;
    }

    return (
      <div className="phone-number-list-wrapper">
        <PhoneNumberListHeader
          search={search}
          onSearchChange={onSearchChangeHandler}
          onFilterClick={handleFilterClick}
          onPurchaseClick={handlePurchaseClick}
          activeFilterCount={activeFilterCount}
          enableBulkActions={enableBulkActions}
          selectedPhoneNumbers={selectedPhoneNumbers}
          deSelectedPhoneNumbers={deSelectedPhoneNumbers}
          isSelectAllAcrossPages={isSelectAllAcrossPages}
          phoneNumberFilters={myPhoneNumbersListFilters}
          refreshPhoneNumbers={refreshPhoneNumbers}
          handleClearSelection={handleClearSelection}
          showAssignModal={showAssignModal}
          showAssignLabelModal={showAssignLabelModal}
          onAssignModalChange={setShowAssignModal}
          onAssignLabelModalChange={setShowAssignLabelModal}
          onSettingsActionChange={setSettingsActionType}
        />
        {!isGetMyPhoneNumbersListRequestPending &&
        myPhoneNumbersList?.total === 0 ? (
          <div className="call-logs__empty-section">
            {(search || activeFilterCount > 0) && (
              <CallLogsSearchEmptyState filterApplied={activeFilterCount > 0} />
            )}
          </div>
        ) : (
          <PhoneNumberListTable
            data={myPhoneNumbersList || undefined}
            paginationOptions={{
              page: pagination.page,
              limit: pagination.limit,
              totalElements: pagination.totalElements,
            }}
            onPaginationOptionsChange={handlePaginationChange}
            isLoading={isGetMyPhoneNumbersListRequestPending}
            selectedRowIds={selectedRowIds}
            onRowSelect={handlePhoneNumberSelect}
            onRowSelectAll={handlePhoneNumberSelectAll}
            onClearSelection={handleClearSelection}
            isSelectAllAcrossPages={isSelectAllAcrossPages}
            onSelectAllAcrossPages={handleSelectAllAcrossPages}
            selectAllDisplayCount={selectAllDisplayCount}
          />
        )}
        {showFiltersModal && (
          <PhoneNumberFiltersModal
            show={showFiltersModal}
            onClose={handleCloseFiltersModal}
            phoneNumberFilters={myPhoneNumbersListFilters}
            onFiltersChange={handleFiltersChange}
          />
        )}
      </div>
    );
  },
);

PhoneNumberList.displayName = 'PhoneNumberList';

export default PhoneNumberList;
